/**
 * ModalShell - Reusable modal template. All modals use this for consistent styling.
 *
 * Rendered through a portal to `document.body` so callers can mount the modal
 * from anywhere in the tree — including inline contexts like a `<p>` of post
 * content (AuraPill) — without producing invalid DOM nesting.
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

function ModalShell({ title, onClose, children, panelClassName = 'max-w-md' }) {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const modal = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-4 pt-safe-top pb-safe-bottom"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="absolute inset-0 bg-void/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className={`relative w-full ${panelClassName} bg-charcoal rounded-2xl border border-shadow shadow-soft p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h2 id="modal-title" className="font-display text-xl font-bold text-ash">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-silver hover:text-ash text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );

  if (typeof document === 'undefined') return modal;
  return createPortal(modal, document.body);
}

export default ModalShell;
